// Dependencies
import { createSlice, createAction } from '@reduxjs/toolkit';
import { PayloadAction } from '@reduxjs/toolkit/dist/createAction';
import { applyColumnResize, ColumnResizePayload } from '../../utils/list/utils/columnResize';
// Types
import { Column } from '../objects/column';
import { GuideRubric } from '../objects/guideRubric';
import { GuideDefaultRubricState } from '../objects/guideDefaultRubricState';
import { Preferences } from '../../profile/objects/preferences';
import { GuideRubricLocalized } from '../objects/guideRubricLocalized';
import { Picture } from '../../picture/objects/picture';

const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');
const addCustomPictures = createAction<Picture[]>('PICTURE_ADD_CUSTOM_PICTURES');

const initialState: GuideDefaultRubricState = {
    columns: [
        {
            label: 'title',
            field_name: 'TITLE',
            checked: true,
            order_by: 'localization__title',
            fixed_width: false,
            last_updated: false,
            width: null
            },
        {
            label: 'created-date',
            field_name: 'CREATED_DATE',
            checked: false,
            order_by: 'created_date',
            fixed_width: true,
            last_updated: false,
            width: null
            },
        {
            label: 'created-by',
            field_name: 'CREATED_BY',
            checked: false,
            order_by: 'creator__username',
            fixed_width: true,
            last_updated: false,
            width: null
            },
        {
            label: 'modified-date',
            field_name: 'MODIFIED_DATE',
            checked: true,
            order_by: 'modified_date',
            fixed_width: true,
            last_updated: false,
            width: null
            },
        {
            label: 'modified-by',
            field_name: 'MODIFIED_BY',
            checked: true,
            order_by: 'last_author__username',
            fixed_width: true,
            last_updated: false,
            width: null
            }
    ],
    count: 0,
    create: false,
    description_locales: {},
    edit: null,
    editor_pictures: null,
    export_full: false,
    export_list: false,
    export_type: 'CSV',
    filter_created_date_gte: null,
    filter_created_date_lte: null,
    filter_creator: '',
    filter_last_author: '',
    filter_modified_date_gte: null,
    filter_modified_date_lte: null,
    filter_open: false,
    filter_period: 'CREATION',
    filter_period_type: 0,
    limit: 50,
    list: null,
    order: 'asc',
    order_by: 'localization__title',
    page: 0,
    preferences: [],
    refresh: false,
    guide_rubric: null,
    running: false,
    search: '',
    title_locales: {},
    on_roadbook: false,
    on_cms: false,
    on_quotation: false,
    on_minisite: false,
    on_good_to_know_roadbook: false,
    on_formalities_roadbook: false
};

export const guideDefaultRubricSlice = createSlice({
    name: 'guide_default_rubric',
    initialState,
    reducers: {
        toggleOnRoadbook: (state) => {
            state.on_roadbook = !state.on_roadbook;
        },
        toggleOnCms: (state) => {
            state.on_cms = !state.on_cms;
        },
        toggleOnFormalitiesRoadbook: (state) => {
            state.on_formalities_roadbook = !state.on_formalities_roadbook;
        },
        toggleCountryInformationMinisite: (state) => {
            state.on_minisite = !state.on_minisite;
        },
        toggleGoodToKnowRb: (state) => {
            state.on_good_to_know_roadbook = !state.on_good_to_know_roadbook;
        },
        toggleOnQuotation: (state) => {
            state.on_quotation = !state.on_quotation;
        },
        addPreferences: (state, action) => {
            state.preferences = [...state.preferences, action.payload];
        },
        moveDownColumn: (state, action) => {
            const columns = [...state.columns];
            for (let i = 0; i < columns.length; i++) {
                columns[i].last_updated = false;
            }
            const column = { ...columns[action.payload + 1] };
            columns[action.payload + 1] = columns[action.payload];
            columns[action.payload + 1].last_updated = true;
            columns[action.payload] = column;
            state.columns = columns;
        },
        moveUpColumn: (state, action) => {
            const columns = [...state.columns];
            for (let i = 0; i < columns.length; i++) {
                columns[i].last_updated = false;
            }
            const column = { ...columns[action.payload - 1] };
            columns[action.payload - 1] = columns[action.payload];
            columns[action.payload - 1].last_updated = true;
            columns[action.payload] = column;
            state.columns = columns;
        },
        resetFilters: (state) => {
            state.filter_created_date_gte = null;
            state.filter_created_date_lte = null;
            state.filter_creator = '';
            state.filter_last_author = '';
            state.filter_modified_date_gte = null;
            state.filter_modified_date_lte = null;
            state.filter_period = 'CREATION';
            state.filter_period_type = 0;
        },
        resetGuideRubric: (state) => {
            state.create = false;
            state.description_locales = {};
            state.edit = null;
            state.editor_pictures = null;
            state.guide_rubric = null;
            state.refresh = !state.refresh;
            state.title_locales = {};
            state.on_quotation = false;
            state.on_roadbook = false;
            state.on_cms = false;
            state.on_minisite = false;
            state.on_good_to_know_roadbook = false;
            state.on_formalities_roadbook = false;
        },
        setEdit: (state, action) => {
            const title_locales: Record<string, string> = {};
            const description_locales: Record<string, string> = {};
            state.guide_rubric?.localization.map((locale: GuideRubricLocalized) => {
                title_locales[locale.locale] = locale.title;
                description_locales[locale.locale] = locale.description;
            });
            state.edit = action.payload;
            state.title_locales = title_locales;
            state.description_locales = description_locales;
        },
        setExportType: (state, action) => {
            state.export_type = action.payload;
        },
        setLimit: (state, action) => {
            state.list = null;
            state.limit = action.payload;
        },
        setList: (state, action) => {
            state.count = action.payload.count;
            state.list = action.payload.results;
            state.running = false;
        },
        setOrderBy: (state, action) => {
            state.list = null;
            state.order_by = action.payload;
            state.order = state.order_by === action.payload && state.order === 'asc' ? 'desc' : 'asc';
        },
        setPage: (state, action) => {
            state.list = null;
            state.page = action.payload;
        },
        setGuideRubric: (state, action: PayloadAction<GuideRubric>) => {
            const title_locales: Record<string, string> = {};
            const description_locales: Record<string, string> = {};
            state.guide_rubric?.localization.map((locale: GuideRubricLocalized) => {
                title_locales[locale.locale] = locale.title;
                description_locales[locale.locale] = locale.description;
            });
            state.edit = null;
            state.title_locales = title_locales;
            state.description_locales = description_locales;
            state.guide_rubric = action.payload;
            state.on_quotation = Boolean(action.payload.on_quotation);
            state.on_roadbook = Boolean(action.payload.on_roadbook);
            state.on_cms = Boolean(action.payload.on_cms);
            state.on_minisite = Boolean(action.payload.on_minisite);
            state.on_good_to_know_roadbook = Boolean(action.payload.on_good_to_know_roadbook);
            state.on_formalities_roadbook = Boolean(action.payload.on_formalities_roadbook);
        },
        setGuideDefaultRubricPreferences: (state, action) => {
            state.preferences = action.payload;
        },
        setRunning: (state, action) => {
            state.running = action.payload;
        },
        setSearch: (state, action) => {
            state.list = null;
            state.page = 0;
            state.search = action.payload;
        },
        toggleColumn: (state, action) => {
            const columns = [...state.columns];
            const column = { ...columns[action.payload] };
            column.checked = !column.checked;
            columns[action.payload] = column;
            state.columns = columns;
        },
        toggleCreate: (state) => {
            state.create = !state.create;
        },
        toggleExport: (state, action) => {
            state.export_list = action.payload;
        },
        toggleExportFull: (state) => {
            state.export_full = !state.export_full;
        },
        toggleFilters: (state) => {
            state.filter_open = !state.filter_open;
        },
        toggleRefresh: (state) => {
            state.refresh = !state.refresh;
        },
        updateDescriptionLocale: (state, action: PayloadAction<{ id: number, value: string }>) => {
            const { id, value } = action.payload;
            const description_locales = { ...state.description_locales };
            description_locales[id] = value;
            state.description_locales = description_locales;
        },
        updateFilterCreatedDateGte: (state, action) => {
            state.filter_created_date_gte = action.payload;
        },
        updateFilterCreator: (state, action) => {
            state.filter_created_date_lte = action.payload;
        },
        updateFilterCreatedDateLte: (state, action) => {
            state.filter_creator = action.payload;
        },
        updateFilterLastAuthor: (state, action) => {
            state.filter_last_author = action.payload;
        },
        updateFilterModifiedDateGte: (state, action) => {
            state.filter_modified_date_gte = action.payload;
        },
        updateFilterModifiedDateLte: (state, action) => {
            state.filter_modified_date_lte = action.payload;
        },
        updateFilterPeriod: (state, action) => {
            state.filter_period = action.payload;
            state.filter_period_type = 0;
            state.filter_created_date_gte = action.payload === 'MODIFICATION' ? null : state.filter_created_date_gte;
            state.filter_created_date_lte = action.payload === 'MODIFICATION' ? null : state.filter_created_date_lte;
            state.filter_modified_date_gte = action.payload === 'CREATION' ? null : state.filter_modified_date_gte;
            state.filter_modified_date_lte = action.payload === 'CREATION' ? null : state.filter_modified_date_lte;
        },
        updateFilterPeriodType: (state, action) => {
            state.filter_period_type = action.payload;
        },
        updateTitleLocale: (state, action: PayloadAction<{ id: number, value: string }>) => {
            const { id, value } = action.payload;
            const title_locales = { ...state.title_locales };
            title_locales[id] = value;
            state.title_locales = title_locales;
        },
        setColumns: (state, action: PayloadAction<Column[]>) => {
            state.columns = action.payload;
        },
        resizeColumn: (state, action: PayloadAction<ColumnResizePayload>) => {
            state.columns = applyColumnResize(state.columns, action.payload);
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(setPreferences, (state, action) => {
                const field_name: string[] = [];
                const columns: Column[] = [];
                const preferences: Preferences[] = [];
                let order_by = '-created_date';
                let order: 'asc' | 'desc' = 'asc';
                action.payload.map((preference: Preferences) => {
                    if (preference.module === 'GUIDE_DEFAULT_RUBRIC') {
                        if (!field_name.includes(preference.field_name)) {
                            switch (preference.field_name) {
                                case 'TITLE':
                                    columns.push({
                                        label: 'title',
                                        field_name: preference.field_name,
                                        checked: preference.active,
                                        order_by: 'localization__title',
                                        fixed_width: false,
                                        last_updated: false,
                                        width: null
                                        });
                                    order_by = preference.ordering_asc !== null ? 'localization__title' : order_by;
                                    break;
                                case 'CREATED_DATE':
                                    columns.push({
                                        label: 'created-date',
                                        field_name: preference.field_name,
                                        checked: preference.active,
                                        order_by: 'created_date',
                                        fixed_width: true,
                                        last_updated: false,
                                        width: null
                                        });
                                    order_by = preference.ordering_asc !== null ? 'created_date' : order_by;
                                    break;
                                case 'CREATED_BY':
                                    columns.push({
                                        label: 'created-by',
                                        field_name: preference.field_name,
                                        checked: preference.active,
                                        order_by: 'creator__username',
                                        fixed_width: false,
                                        last_updated: false,
                                        width: null
                                        });
                                    order_by = preference.ordering_asc !== null ? 'creator__username' : order_by;
                                    break;
                                case 'MODIFIED_DATE':
                                    columns.push({
                                        label: 'modified-date',
                                        field_name: preference.field_name,
                                        checked: preference.active,
                                        order_by: 'modified_date',
                                        fixed_width: true,
                                        last_updated: false,
                                        width: null
                                        });
                                    order_by = preference.ordering_asc !== null ? 'modified_date' : order_by;
                                    break;
                                case 'MODIFIED_BY':
                                    columns.push({
                                        label: 'modified-by',
                                        field_name: preference.field_name,
                                        checked: preference.active,
                                        order_by: 'last_author__username',
                                        fixed_width: false,
                                        last_updated: false,
                                        width: null
                                        });
                                    order_by = preference.ordering_asc !== null ? 'last_author__username' : order_by;
                                    break;
                                default:
                                    break;
                            }
                            preferences.push(preference);
                            field_name.push(preference.field_name);
                            order = preference.ordering_asc !== null ? preference.ordering_asc ? 'asc' : 'desc' : order;
                        }
                    }
                });
                if (columns.length !== state.columns.length) {
                    state.columns.map((column) => {
                        let added = false;
                        for (let i = 0; i < columns.length; i++) {
                            if (columns[i].label === column.label) {
                                added = true;
                                break;
                            }
                        }
                        if (!added) {
                            columns.push({ ...column });
                        }
                    });
                }
                state.preferences = preferences;
                state.columns = columns;
                state.order_by = order_by;
                state.order = order;
            })
            .addCase(addCustomPictures, (state, action) => {
                if (state.guide_rubric !== null || state.create) {
                    state.editor_pictures = [...action.payload];
                }
            })
            .addDefaultCase(() => {});
    }
});

export const {
    addPreferences,
    moveDownColumn,
    moveUpColumn,
    resetFilters,
    resetGuideRubric,
    setEdit,
    setExportType,
    setLimit,
    setList,
    setOrderBy,
    setPage,
    setGuideRubric,
    setGuideDefaultRubricPreferences,
    setRunning,
    setSearch,
    toggleColumn,
    toggleCreate,
    toggleExport,
    toggleExportFull,
    toggleFilters,
    toggleRefresh,
    updateDescriptionLocale,
    updateFilterCreatedDateGte,
    updateFilterCreator,
    updateFilterCreatedDateLte,
    updateFilterLastAuthor,
    updateFilterModifiedDateGte,
    updateFilterModifiedDateLte,
    updateFilterPeriod,
    updateFilterPeriodType,
    updateTitleLocale,
    setColumns,
    resizeColumn,
    toggleOnRoadbook,
    toggleOnCms,
    toggleCountryInformationMinisite,
    toggleGoodToKnowRb,
    toggleOnFormalitiesRoadbook,
    toggleOnQuotation
} = guideDefaultRubricSlice.actions;

export default guideDefaultRubricSlice.reducer;